import { t } from '../i18n.js';
import { apiGet, markRemindersRead, snoozeReminder, ApiError } from '../api.js';
import { bottomSheet } from './bottom-sheet.js';
import { toast } from './toast.js';
import { isDesktopViewport } from './breakpoint.js';


// Module-level registry so pages can request a bell badge refresh without
// holding a direct reference to the bell instance.
let _bellRefresh = () => Promise.resolve();

/**
 * Request a bell badge refresh from any page. Layout.js wires the current
 * bell instance's refresh() into this after creating the bell.
 */
export function refreshBell() {
  return _bellRefresh();
}

/**
 * Format a remind_at / expiry string into a human-readable "expires in X days"
 * label, using the reminder's lead_time_days or the remind_at date itself.
 *
 * @param {object} reminder  Serialized reminder from GET /api/v1/reminders
 * @returns {string}
 */
function formatWhen(reminder) {
  const remindAt = reminder.remind_at ? new Date(reminder.remind_at) : null;
  if (!remindAt || Number.isNaN(remindAt.getTime())) return '';

  const now = new Date();
  const msDiff = remindAt.getTime() - now.getTime();
  const daysDiff = Math.round(msDiff / (1000 * 60 * 60 * 24));

  if (daysDiff <= 0) return t('reminders.expires_today');
  if (daysDiff === 1) return t('reminders.expires_tomorrow');
  return t('reminders.expires_in', { days: String(daysDiff) });
}

/**
 * Validate that the given datetime-local string is in the future and within 30 days.
 * Returns an error message key or null if valid.
 *
 * @param {string} localDatetimeValue  Value from datetime-local input (e.g. "2026-06-04T08:00")
 * @returns {string|null}  Error i18n key or null if valid
 */
function validateSnoozeUntil(localDatetimeValue) {
  if (!localDatetimeValue) return 'reminders.snooze_error_past';
  const picked = new Date(localDatetimeValue);
  if (Number.isNaN(picked.getTime())) return 'reminders.snooze_error_past';
  const now = Date.now();
  if (picked.getTime() <= now) return 'reminders.snooze_error_past';
  const maxMs = now + 30 * 24 * 60 * 60 * 1000;
  if (picked.getTime() > maxMs) return 'reminders.snooze_error_too_long';
  return null;
}

/**
 * Convert a datetime-local input value to an ISO-8601 string (UTC).
 * The input value is local time; we treat it as the user's local timezone.
 *
 * @param {string} localDatetimeValue
 * @returns {string}
 */
function localDatetimeToIso(localDatetimeValue) {
  return new Date(localDatetimeValue).toISOString();
}

/**
 * Build and return the "Snooze until…" picker form element.
 * On confirm, calls onConfirm(isoString). On cancel, calls onCancel().
 * Client-validates that the time is in the future and <= 30 days.
 *
 * @param {function} onConfirm  Called with the ISO-8601 string
 * @param {function} onCancel   Called when picker is dismissed
 * @returns {HTMLElement}
 */
function buildSnoozePicker(onConfirm, onCancel) {
  const form = document.createElement('div');
  form.setAttribute('data-role', 'snooze-picker-form');
  form.className = 'flex flex-col gap-2 mt-2';

  const dtInput = document.createElement('input');
  dtInput.type = 'datetime-local';
  dtInput.className = 'input text-sm';

  const errorEl = document.createElement('p');
  errorEl.className = 'text-xs text-danger dark:text-danger-400 hidden';
  errorEl.setAttribute('aria-live', 'polite');

  const actions = document.createElement('div');
  actions.className = 'flex gap-2';

  const confirmBtn = document.createElement('button');
  confirmBtn.type = 'button';
  confirmBtn.setAttribute('data-role', 'snooze-picker-confirm');
  confirmBtn.className = 'btn-primary text-sm';
  confirmBtn.textContent = t('reminders.snooze_picker_confirm');

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn-ghost text-sm';
  cancelBtn.textContent = t('actions.cancel');

  actions.append(confirmBtn, cancelBtn);
  form.append(dtInput, errorEl, actions);

  confirmBtn.addEventListener('click', () => {
    const err = validateSnoozeUntil(dtInput.value);
    if (err) {
      errorEl.textContent = t(err);
      errorEl.classList.remove('hidden');
      return;
    }
    errorEl.classList.add('hidden');
    onConfirm(localDatetimeToIso(dtInput.value));
  });

  cancelBtn.addEventListener('click', () => {
    onCancel();
  });

  return form;
}

/**
 * Render a single reminder row element for the desktop dropdown (>= lg).
 * Includes inline snooze actions.
 *
 * @param {object} reminder  Serialized reminder
 * @param {function} onSnoozeSuccess  Called after a successful snooze
 * @returns {HTMLElement}
 */
function buildReminderRow(reminder, onSnoozeSuccess) {
  const row = document.createElement('div');
  row.className = 'flex flex-col gap-0.5 px-3 py-2 hover:bg-primary-50 dark:hover:bg-white/10 rounded text-sm';

  const top = document.createElement('div');
  top.className = 'flex items-center justify-between gap-2';

  const productEl = document.createElement('span');
  productEl.className = 'font-medium truncate';
  productEl.textContent = reminder.product_name || t('inventory.item_label');

  const whenEl = document.createElement('span');
  whenEl.className = 'text-xs text-ink-muted shrink-0';
  whenEl.textContent = formatWhen(reminder);

  top.append(productEl, whenEl);
  row.append(top);

  if (reminder.storage_location_name) {
    const loc = document.createElement('div');
    loc.className = 'text-xs text-ink-muted truncate';
    loc.textContent = reminder.storage_location_name;
    row.append(loc);
  }

  // ----- Inline snooze actions (>= lg) --------------------------------
  const snoozeBar = document.createElement('div');
  snoozeBar.className = 'flex items-center gap-1 mt-1 flex-wrap';

  const snooze1hBtn = document.createElement('button');
  snooze1hBtn.type = 'button';
  snooze1hBtn.setAttribute('data-role', 'snooze-1h');
  snooze1hBtn.className = 'btn-ghost text-xs py-0.5 px-2';
  snooze1hBtn.textContent = t('reminders.snooze_1h');

  const snooze1dBtn = document.createElement('button');
  snooze1dBtn.type = 'button';
  snooze1dBtn.setAttribute('data-role', 'snooze-1d');
  snooze1dBtn.className = 'btn-ghost text-xs py-0.5 px-2';
  snooze1dBtn.textContent = t('reminders.snooze_1d');

  const snoozeUntilBtn = document.createElement('button');
  snoozeUntilBtn.type = 'button';
  snoozeUntilBtn.setAttribute('data-role', 'snooze-until');
  snoozeUntilBtn.className = 'btn-ghost text-xs py-0.5 px-2';
  snoozeUntilBtn.textContent = t('reminders.snooze_until');

  snoozeBar.append(snooze1hBtn, snooze1dBtn, snoozeUntilBtn);
  row.append(snoozeBar);

  // Picker form slot (hidden by default, shown when "Snooze until…" is clicked)
  let pickerEl = null;

  async function doSnooze(opts) {
    try {
      const result = await snoozeReminder(reminder.id, opts);
      if (result && typeof result.unread_count === 'number') {
        if (onSnoozeSuccess) onSnoozeSuccess(result.unread_count);
      }
    } catch (err) {
      if (err && err.code === 'snooze_until_in_past') {
        toast.show(t('reminders.snooze_error_past'), 'error');
      } else if (err && err.code === 'snooze_too_long') {
        toast.show(t('reminders.snooze_error_too_long'), 'error');
      } else {
        toast.show(t('errors.server'), 'error');
      }
    }
  }

  snooze1hBtn.addEventListener('click', () => doSnooze({ duration: '1h' }));
  snooze1dBtn.addEventListener('click', () => doSnooze({ duration: '1d' }));

  snoozeUntilBtn.addEventListener('click', () => {
    // Remove previous picker if any
    if (pickerEl) {
      pickerEl.remove();
      pickerEl = null;
      return;
    }
    pickerEl = buildSnoozePicker(
      async (iso) => {
        pickerEl.remove();
        pickerEl = null;
        await doSnooze({ until: iso });
      },
      () => {
        if (pickerEl) {
          pickerEl.remove();
          pickerEl = null;
        }
      },
    );
    row.append(pickerEl);
  });

  return row;
}

/**
 * Sort reminders by remind_at ascending (soonest first) and return the first 5
 * unread reminders.
 *
 * "Unread" is the per-user `read` overlay flag — NOT the dispatch-lifecycle
 * `status`. The worker flips due reminders to status="dispatched" after sending
 * them, but they still count toward the badge (`unread_count`) until the user
 * reads them, so both "pending" and "dispatched" must be shown here. Filtering
 * on `status === 'pending'` alone hid dispatched reminders that the badge still
 * counted, producing a badge of "1" over an empty "No notifications" panel.
 *
 * @param {Array<object>} reminders
 * @returns {Array<object>}
 */
function topUnread(reminders) {
  return reminders
    .filter((r) => (r.status === 'pending' || r.status === 'dispatched') && r.read === false)
    .sort((a, b) => {
      const ta = a.remind_at ? new Date(a.remind_at).getTime() : Infinity;
      const tb = b.remind_at ? new Date(b.remind_at).getTime() : Infinity;
      return ta - tb;
    })
    .slice(0, 5);
}

/**
 * Header bell + dropdown / bottom-sheet component. Fetches GET /api/v1/reminders
 * on click. On >= lg renders an inline dropdown; on < lg opens a bottom sheet.
 *
 * @returns {{ el: HTMLElement, refresh(): Promise<void> }}
 */
export function reminderBell() {
  const wrap = document.createElement('div');
  wrap.className = 'relative';

  // ----- Trigger --------------------------------------------------------
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.setAttribute('data-reminder-bell-trigger', '');
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.className = 'btn relative';

  const bellIcon = document.createElement('span');
  bellIcon.setAttribute('aria-hidden', 'true');
  bellIcon.textContent = '🔔';

  const badge = document.createElement('span');
  badge.setAttribute('data-reminder-badge', '');
  badge.className = [
    'absolute -top-1 -right-1 hidden',
    'min-w-[1.125rem] h-[1.125rem] px-0.5',
    'rounded-full bg-danger text-white text-[10px] font-bold',
    'flex items-center justify-center leading-none',
  ].join(' ');

  trigger.append(bellIcon, badge);
  wrap.append(trigger);

  // ----- Dropdown panel (desktop >= lg) ---------------------------------
  const panel = document.createElement('div');
  panel.setAttribute('data-reminder-bell-panel', '');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', t('reminders.bell_label'));
  panel.className = [
    'hidden absolute right-0 mt-2 z-50 w-80 anim-pop',
    'bg-surface-light dark:bg-surface-card-dark',
    'border border-border dark:border-border-dark rounded-lg shadow-lg',
    'overflow-hidden text-sm text-ink dark:text-ink-on-dark',
  ].join(' ');

  const panelHeader = document.createElement('div');
  panelHeader.className = 'flex items-center justify-between px-3 py-2 border-b border-border dark:border-border-dark';
  const panelTitle = document.createElement('span');
  panelTitle.className = 'font-semibold text-xs uppercase tracking-wide text-ink-muted';
  panelTitle.textContent = t('reminders.page_title');
  panelHeader.append(panelTitle);
  panel.append(panelHeader);

  const listEl = document.createElement('div');
  listEl.setAttribute('data-reminder-list', '');
  listEl.className = 'py-1 max-h-72 overflow-y-auto';
  panel.append(listEl);

  const footerEl = document.createElement('div');
  footerEl.className = 'border-t border-border dark:border-border-dark px-3 py-2 text-center';
  const seeAllLink = document.createElement('a');
  seeAllLink.href = '#/reminders';
  seeAllLink.className = 'text-xs text-primary hover:underline';
  seeAllLink.textContent = t('reminders.see_all');
  seeAllLink.addEventListener('click', () => closeDropdown());
  footerEl.append(seeAllLink);
  panel.append(footerEl);

  wrap.append(panel);

  // ----- State ----------------------------------------------------------
  let dropdownOpen = false;
  let onDocMouseDown = null;
  let onKeyDown = null;
  let onHashChange = null;

  /** @type {Array<object>} */
  let cachedReminders = [];
  let cachedUnreadCount = 0;

  // ----- Badge update ---------------------------------------------------
  function updateBadge(unreadCount) {
    cachedUnreadCount = unreadCount;
    trigger.setAttribute(
      'aria-label',
      unreadCount > 0
        ? t('reminders.unread_count', { count: String(unreadCount) })
        : t('reminders.bell_label'),
    );
    if (unreadCount > 0) {
      badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }

  // ----- Populate list --------------------------------------------------
  function populateList(container, reminders, onSnoozeSuccess) {
    while (container.firstChild) container.removeChild(container.firstChild);

    const unread = topUnread(reminders);
    if (unread.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'px-3 py-4 text-center text-xs text-ink-muted';
      empty.textContent = t('reminders.no_reminders');
      container.append(empty);
      return;
    }

    for (const reminder of unread) {
      container.append(buildReminderRow(reminder, onSnoozeSuccess));
    }
  }

  // ----- Fetch ----------------------------------------------------------
  async function fetchReminders() {
    try {
      const data = await apiGet('/reminders');
      if (data && Array.isArray(data.reminders)) {
        cachedReminders = data.reminders;
        updateBadge(data.unread_count ?? 0);
      }
    } catch {
      // Silently fail — the bell is a non-critical enhancement
    }
  }

  // ----- Auto-mark-on-view -----------------------------------------------
  /**
   * After the bell opens, POST the unread rendered reminder ids to mark-read
   * and update the badge from the per-user unread_count returned by the server.
   * Only fires when there are actually unread items to mark (avoids a no-op POST).
   *
   * @param {Array<object>} rendered  The reminder objects that were just shown
   */
  async function markRenderedRead(rendered) {
    const unreadIds = rendered.filter((r) => r.read === false).map((r) => r.id);
    if (unreadIds.length === 0) return;
    try {
      const result = await markRemindersRead({ ids: unreadIds });
      if (result && typeof result.unread_count === 'number') {
        updateBadge(result.unread_count);
      }
    } catch {
      // Non-critical: badge may be slightly stale; silently ignore
    }
  }

  // ----- Snooze success handler -----------------------------------------
  /**
   * Called after a snooze succeeds. Re-fetches so the snoozed row disappears
   * and updates the badge.
   *
   * @param {number} unreadCount  New unread count from snooze response
   */
  async function onSnoozeSuccess(unreadCount) {
    updateBadge(unreadCount);
    await fetchReminders();
    // Re-populate the dropdown if it's open
    if (dropdownOpen) {
      populateList(listEl, cachedReminders, onSnoozeSuccess);
    }
  }

  // ----- Dropdown open/close (>= lg only) -------------------------------
  function openDropdown() {
    if (dropdownOpen) return;
    dropdownOpen = true;
    populateList(listEl, cachedReminders, onSnoozeSuccess);
    panel.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');

    onDocMouseDown = (ev) => {
      if (!wrap.contains(ev.target)) closeDropdown();
    };
    onKeyDown = (ev) => {
      if (ev.key === 'Escape') {
        closeDropdown();
        trigger.focus();
      }
    };
    onHashChange = () => closeDropdown();

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('hashchange', onHashChange);
  }

  function closeDropdown() {
    if (!dropdownOpen) return;
    dropdownOpen = false;
    panel.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');

    if (onDocMouseDown) document.removeEventListener('mousedown', onDocMouseDown);
    if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
    if (onHashChange) window.removeEventListener('hashchange', onHashChange);
    onDocMouseDown = onKeyDown = onHashChange = null;
  }

  // ----- Bottom sheet (< lg) -------------------------------------------
  function openSheet() {
    const unread = topUnread(cachedReminders);

    // Build a custom footer with "See all" link
    const sheetFooter = document.createElement('div');
    sheetFooter.className = 'pt-2 pb-1 text-center border-t border-border dark:border-border-dark mt-1';
    const sheetSeeAll = document.createElement('a');
    sheetSeeAll.href = '#/reminders';
    sheetSeeAll.className = 'text-xs text-primary hover:underline';
    sheetSeeAll.textContent = t('reminders.see_all');
    sheetFooter.append(sheetSeeAll);

    if (unread.length === 0) {
      // No-item case: use a single informational action row
      bottomSheet({
        title: t('reminders.page_title'),
        rows: [
          {
            kind: 'action',
            label: t('reminders.no_reminders'),
            onSelect: () => {},
          },
        ],
        footer: sheetFooter,
      });
      return;
    }

    // Build flat rows: per reminder — its label (navigate on click) +
    // three snooze action rows. This way snooze rows appear directly
    // in the sheet without a sub-sheet.
    const rows = [];
    for (const reminder of unread) {
      // Reminder label row (tapping navigates to /reminders)
      rows.push({
        kind: 'action',
        label: [
          reminder.product_name || t('inventory.item_label'),
          reminder.storage_location_name ? ` — ${reminder.storage_location_name}` : '',
          `  ${formatWhen(reminder)}`,
        ].join('').trim(),
        onSelect: () => {
          window.location.hash = '#/reminders';
        },
      });

      // Snooze preset rows — use dataRole so we can identify them in tests
      rows.push({
        kind: 'action',
        label: t('reminders.snooze_1h'),
        dataRole: 'snooze-sheet-row',
        onSelect: () => doSnoozeFromSheet(reminder.id, { duration: '1h' }),
      });
      rows.push({
        kind: 'action',
        label: t('reminders.snooze_1d'),
        dataRole: 'snooze-sheet-row',
        onSelect: () => doSnoozeFromSheet(reminder.id, { duration: '1d' }),
      });
      rows.push({
        kind: 'action',
        label: t('reminders.snooze_until'),
        dataRole: 'snooze-sheet-row',
        onSelect: () => openSheetSnoozePicker(reminder.id),
      });
    }

    bottomSheet({
      title: t('reminders.page_title'),
      rows,
      footer: sheetFooter,
    });

    // Post-process: set data-snooze-action on the snooze rows so tests
    // can find them by action type. Snooze rows repeat in groups of 3
    // (1h, 1d, until) after each reminder label row.
    const snoozeActionValues = ['1h', '1d', 'until'];
    const sheetRows = document.querySelectorAll('[data-role="snooze-sheet-row"]');
    sheetRows.forEach((btn, i) => {
      btn.dataset.snoozeAction = snoozeActionValues[i % 3];
    });
  }

  /**
   * Perform snooze from mobile sheet action (preset durations).
   * The sheet already closed when onSelect was called; re-fetch on success.
   */
  async function doSnoozeFromSheet(reminderId, opts) {
    try {
      const result = await snoozeReminder(reminderId, opts);
      if (result && typeof result.unread_count === 'number') {
        updateBadge(result.unread_count);
      }
      await fetchReminders();
    } catch (err) {
      if (err && err instanceof ApiError) {
        if (err.code === 'snooze_until_in_past') {
          toast.show(t('reminders.snooze_error_past'), 'error');
        } else if (err.code === 'snooze_too_long') {
          toast.show(t('reminders.snooze_error_too_long'), 'error');
        } else {
          toast.show(t('errors.server'), 'error');
        }
      }
    }
  }

  /**
   * Open a "Snooze until…" date-picker sheet for a reminder (mobile).
   * Called after the main snooze-options sheet has closed.
   */
  function openSheetSnoozePicker(reminderId) {
    const picker = buildSnoozePicker(
      async (iso) => {
        // Close the picker sheet first (it will already be closed by bottomSheet's onSelect)
        await doSnoozeFromSheet(reminderId, { until: iso });
      },
      () => {},
    );

    // Wrap the picker in an action sheet footer for visual framing
    bottomSheet({
      title: t('reminders.snooze_until'),
      rows: [
        {
          kind: 'action',
          label: t('actions.cancel'),
          onSelect: () => {},
        },
      ],
      footer: picker,
    });
  }

  // ----- Trigger click --------------------------------------------------
  trigger.addEventListener('click', async () => {
    // Always fetch fresh data on open
    await fetchReminders();

    const isDesktop = isDesktopViewport();
    if (isDesktop) {
      if (dropdownOpen) {
        closeDropdown();
        return;
      }
      openDropdown();
    } else {
      openSheet();
    }

    // Auto-mark-on-view: mark the reminders the user just saw as read
    const rendered = topUnread(cachedReminders);
    await markRenderedRead(rendered);
  });

  // Initialise badge on mount with a silent background fetch
  fetchReminders();

  // Register this instance's refresh as the global bell refresher
  _bellRefresh = fetchReminders;

  return {
    el: wrap,
    /** Re-fetch and update the badge (e.g. after inventory mutations). */
    refresh: fetchReminders,
    /**
     * Release the document- and window-level listeners the open panel installs.
     * A shell remount discards this node without closing the panel first, which
     * orphaned three listeners per open bell on every locale switch, login and
     * profile save.
     */
    destroy: () => { closeDropdown(); },
  };
}
